import { useState } from "react";
import {
	Button,
	Grid,
	TextField,
	Stack,
	Autocomplete,
	FormGroup,
	FormControlLabel,
	Switch,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import greekPrefectures from "./greekPrefectures";
import municipalities from "./municipalities";

const fields = [
	{ name: "aa", label: "Α/Α", type: "number" },
	{ name: "mitroou", label: "Μητρώο" },
	{ name: "kaek", label: "KAEK" },
	{ name: "abk", label: "ABK" },
	{ name: "x", label: "X", type: "number" },
	{ name: "y", label: "Y", type: "number" },
	{ name: "country", label: "Χώρα" },
	{ name: "nomos", label: "Νομός" },
	{ name: "dimos", label: "Δήμος" },
	{ name: "thesi", label: "Θέση" },
	{ name: "topos", label: "Τόπος" },
	{ name: "oikismos", label: "Οικισμός" },
	{ name: "tk", label: "Τκ" },
	{ name: "odos", label: "Οδός" },
	{ name: "arithmos", label: "Αριθμός" },
	{ name: "kathestos", label: "Καθεστώς" },
	{ name: "proelefsi", label: "Προέλευση" },
	{ name: "perigrafi", label: "Περιγραφή" },
	{ name: "geofysikos_xaraktiras", label: "Γεωφυσικός χαρακτήρας" },
	{ name: "trexousa_xrisi", label: "Τρέχουσα χρήση" },
	{ name: "alli_xrisi", label: "Άλλη χρήση" },
	{ name: "xaraktiras_ktimatos", label: "Χαρακτήρας κτήματος" },
	{ name: "poleodomikos_xaraktiras", label: "Πολεοδομικός χαρακτήρας" },
	{ name: "poleodomikes_desmefseis", label: "Πολεοδομικές δεσμεύσεις" },
	{ name: "empragmata_dikaiomata", label: "Εμπράγματα δικαιώματα" },
	{ name: "gipeda", label: "Γήπεδα" },
	{ name: "syndeseis", label: "Συνδέσεις", type: "multiselect", options: ["Ρεύμα", "Νερό", "Τηλέφωνο", "Ιντερνετ"] },
	{ name: "emvado_ktimatos", label: "Εμβαδό κτήματος σε τμ", type: "number" },
	{ name: "pososto_kalypsis", label: "Ποσοστό κάλυψης (%)", type: "number" },
	{ name: "syntelestis_domisis", label: "Συντελεστής δόμησης", type: "number" },
	{ name: "foreas_idioktitis", label: "Φορέας ιδιοκτήτης" },
	{ name: "foreas_diaxeirisis", label: "Φορέας διαχείρισης" },
	{ name: "xristis_akinitou", label: "Χρήστης ακινήτου" },
	{ name: "antikeimeniki_axia", label: "Αντικειμενική αξία €", type: "number" },
	{ name: "imnia_ypologismou_axias", label: "Ημ/νία υπολογισμού αξίας", type: "date" },
	{ name: "misthoma", label: "Μίσθωμα", type: "number" },
	{ name: "imnia_enarxis_misthotiriou", label: "Ημ/νία έναρξης μισθωτηρίου", type: "date" },
	{ name: "paratiriseis", label: "Παρατηρήσεις" },
	{ name: "typos_ktirioy", label: "Τύπος κτιρίου" },
	{ name: "etos_kataskevis", label: "Έτος κατασκευής", type: "number" },
	{ name: "orofos", label: "Όροφος", type: "number" },
	{ name: "emvado_ktiriou", label: "Εμβαδό κτιρίου", type: "number" },
];

const buildingFields = ["typos_ktirioy", "etos_kataskevis", "orofos", "emvado_ktiriou"];

export default function PropertyForm({
	initialData = {},
	onSubmit,
	onCancel,
	allOpen,
	handleAllOpenChange,
	expandedAccordions,
	setExpandedAccordions,
}) {
	const [savingStatus, setSavingStatus] = useState(false);
	const [values, setValues] = useState(() => {
		const v = {};
		const today = dayjs();
		fields.forEach((f) => {
			if (f.name === "imnia_ypologismou_axias" || f.name === "imnia_enarxis_misthotiriou") {
				// Convert ISO date to dayjs object for DatePicker
				if (initialData && initialData[f.name] != null) {
					try {
						const date = dayjs(initialData[f.name]);
						v[f.name] = date.isValid() ? date : today;
					} catch (error) {
						v[f.name] = today;
					}
				} else {
					v[f.name] = null;
				}
			} else if (f.name === "country") {
				v[f.name] = initialData && initialData[f.name] != null ? initialData[f.name] : "Ελλάδα";
			} else if (f.name === "etos_kataskevis") {
				v[f.name] =
					initialData && initialData[f.name] != null && initialData[f.name] !== 0 ? initialData[f.name] : 0;
			} else {
				v[f.name] =
					initialData && initialData[f.name] != null ? initialData[f.name] : f.type === "number" ? 0 : "";
			}
		});

		return v;
	});

	const isEdit = initialData && initialData.aa != null;

	const handleChange = (e) => {
		const { name, value, type } = e.target;
		setValues((v) => {
			if (name === "nomos") {
				return { ...v, nomos: value, dimos: "" };
			}
			if (name === "etos_kataskevis") {
				return { ...v, [name]: value === "" || value === "0" ? "" : Number(value) };
			}
			return { ...v, [name]: type === "number" ? Number(value) : value };
		});
	};

	const handleDateChange = (name) => (date) => {
		setValues((v) => ({ ...v, [name]: date }));
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const submitValues = { ...values };
		if (Array.isArray(submitValues.syndeseis)) {
			submitValues.syndeseis = submitValues.syndeseis.join(",");
		}

		// Convert dayjs objects to ISO format for date fields
		["imnia_ypologismou_axias", "imnia_enarxis_misthotiriou"].forEach((dateField) => {
			if (submitValues[dateField] && dayjs.isDayjs(submitValues[dateField])) {
				submitValues[dateField] = dayjs(submitValues[dateField]).format("YYYY-MM-DD").toString();
			}
		});

		// console.log("submitValues", submitValues);
		setSavingStatus(true);
		onSubmit(submitValues);
	};

	// Find the index of the 'gipeda' field
	const gipedaIdx = fields.findIndex((f) => f.name === "gipeda");

	// Define the list of fields for the new accordion group, excluding the building fields
	const propertyAccordionFields = [
		"emvado_ktimatos",
		"pososto_kalypsis",
		"syntelestis_domisis",
		"foreas_idioktitis",
		"foreas_diaxeirisis",
		"xristis_akinitou",
		"antikeimeniki_axia",
		"imnia_ypologismou_axias",
		"misthoma",
		"imnia_enarxis_misthotiriou",
		"paratiriseis",
	];

	// Define the list of fields for the basic accordion group
	const basicAccordionFields = fields
		.map((f) => f.name)
		.filter(
			(name) =>
				name !== "aa" && ![...buildingFields, ...propertyAccordionFields, "gipeda", "syndeseis"].includes(name)
		);

	// Accept expandedAccordions and setExpandedAccordions as props
	// Use expandedAccordions and setExpandedAccordions for each Accordion's expanded and onChange props
	// Use allOpen only for the switch and for handleAllOpenChange logic

	const handleAccordionChange = (key) => (event, isExpanded) => {
		setExpandedAccordions((prev) => ({ ...prev, [key]: isExpanded }));
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<form onSubmit={handleSubmit}>
				<Grid container spacing={2}>
					{/* Only render the 'aa' field outside accordions */}
					<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
						<TextField fullWidth label="Α/Α" name="aa" type="number" value={values["aa"] || ""} disabled />
						<input type="hidden" name="aa" value={values["aa"] || ""} />
					</Grid>

					{/* Basic Accordion */}
					<Accordion
						sx={{ width: "100%", my: 2, boxShadow: "none", border: "none" }}
						expanded={expandedAccordions.basic}
						onChange={handleAccordionChange("basic")}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse" }}>
							<Typography sx={{ fontWeight: "bold", color: "#003375", fontSize: "14px" }}>
								Βασικά Στοιχεία
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={2}>
								<Grid size={2} key="mitroou">
									<TextField
										fullWidth
										label="Μητρώο"
										name="mitroou"
										value={values["mitroou"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="kaek">
									<TextField
										fullWidth
										label="ΚΑΕΚ"
										name="kaek"
										value={values["kaek"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="abk">
									<TextField
										fullWidth
										label="ΑΒΚ"
										name="abk"
										value={values["abk"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="x">
									<TextField
										fullWidth
										label="X"
										name="x"
										value={values["x"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="y">
									<TextField
										fullWidth
										label="Y"
										name="y"
										value={values["y"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="country">
									<TextField
										fullWidth
										label="Χώρα"
										name="country"
										value={values["country"]}
										disabled
									/>
								</Grid>
								<Grid size={2} key="nomos">
									<Autocomplete
										options={greekPrefectures}
										value={values["nomos"] || ""}
										onChange={(_, newValue) =>
											setValues((v) => ({ ...v, nomos: newValue, dimos: "" }))
										}
										renderInput={(params) => <TextField {...params} label="Νομός" fullWidth />}
										disableClearable
										isOptionEqualToValue={(option, value) => option === value}
									/>
								</Grid>
								<Grid size={2} key="dimos">
									<Autocomplete
										options={municipalities[values["nomos"]] || []}
										value={values["dimos"] || ""}
										onChange={(_, newValue) => setValues((v) => ({ ...v, dimos: newValue }))}
										renderInput={(params) => <TextField {...params} label="Δήμος" fullWidth />}
										disabled={!values["nomos"]}
										disableClearable
										isOptionEqualToValue={(option, value) => option === value}
									/>
								</Grid>
								<Grid size={2} key="thesi">
									<TextField
										fullWidth
										label="Θέση"
										name="thesi"
										value={values["thesi"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="topos">
									<TextField
										fullWidth
										label="Τόπος"
										name="topos"
										value={values["topos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="oikismos">
									<TextField
										fullWidth
										label="Οικισμός"
										name="oikismos"
										value={values["oikismos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="tk">
									<TextField
										fullWidth
										label="Τκ"
										name="tk"
										type="number"
										value={values["tk"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="odos">
									<TextField
										fullWidth
										label="Οδός"
										name="odos"
										value={values["odos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="arithmos">
									<TextField
										fullWidth
										label="Αριθμός"
										name="arithmos"
										value={values["arithmos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="kathestos">
									<TextField
										fullWidth
										label="Καθεστώς"
										name="kathestos"
										value={values["kathestos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="proelefsi">
									<TextField
										fullWidth
										label="Προέλευση"
										name="proelefsi"
										value={values["proelefsi"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="perigrafi">
									<TextField
										fullWidth
										label="Περιγραφή"
										name="perigrafi"
										value={values["perigrafi"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="geofysikos_xaraktiras">
									<TextField
										fullWidth
										label="Γεωφυσικός χαρακτήρας"
										name="geofysikos_xaraktiras"
										value={values["geofysikos_xaraktiras"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="trexousa_xrisi">
									<TextField
										fullWidth
										label="Τρέχουσα χρήση"
										name="trexousa_xrisi"
										value={values["trexousa_xrisi"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="alli_xrisi">
									<TextField
										fullWidth
										label="Άλλη χρήση"
										name="alli_xrisi"
										value={values["alli_xrisi"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="xaraktiras_ktimatos">
									<TextField
										fullWidth
										label="Χαρακτήρας κτήματος"
										name="xaraktiras_ktimatos"
										value={values["xaraktiras_ktimatos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="poleodomikos_xaraktiras">
									<TextField
										fullWidth
										label="Πολεοδομικός χαρακτήρας"
										name="poleodomikos_xaraktiras"
										value={values["poleodomikos_xaraktiras"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="poleodomikes_desmefseis">
									<TextField
										fullWidth
										label="Πολεοδομικές δεσμεύσεις"
										name="poleodomikes_desmefseis"
										value={values["poleodomikes_desmefseis"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2} key="empragmata_dikaiomata">
									<TextField
										fullWidth
										label="Εμπράγματα δικαιώματα"
										name="empragmata_dikaiomata"
										value={values["empragmata_dikaiomata"]}
										onChange={handleChange}
									/>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>

					<Accordion
						sx={{ width: "100%", my: 2, boxShadow: "none", border: "none" }}
						expanded={expandedAccordions.building}
						onChange={handleAccordionChange("building")}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse" }}>
							<Typography sx={{ fontWeight: "bold", color: "#003375", fontSize: "14px" }}>
								Κτίριο & Γήπεδα
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={2}>
								<Grid size={2}>
									<TextField
										fullWidth
										label="Τύπος κτιρίου"
										name="typos_ktirioy"
										value={values["typos_ktirioy"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2}>
									<TextField
										fullWidth
										label="Έτος κατασκευής"
										name="etos_kataskevis"
										type="number"
										value={values["etos_kataskevis"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2}>
									<TextField
										fullWidth
										label="Όροφος"
										name="orofos"
										type="number"
										value={values["orofos"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={2}>
									<TextField
										fullWidth
										label="Εμβαδό κτιρίου"
										name="emvado_ktiriou"
										type="number"
										value={values["emvado_ktiriou"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={4}>
									<TextField
										fullWidth
										label="Γήπεδα"
										name="gipeda"
										value={values["gipeda"]}
										onChange={handleChange}
									/>
								</Grid>
								<Grid size={{ xs: 12 }}>
									<FormGroup row>
										{fields
											.find((f) => f.name === "syndeseis")
											.options.map((option) => (
												<FormControlLabel
													key={option}
													control={
														<Switch
															checked={
																Array.isArray(values.syndeseis)
																	? values.syndeseis.includes(option)
																	: (values.syndeseis || "")
																			.split(",")
																			.includes(option)
															}
															onChange={(_, checked) => {
																let newArr = Array.isArray(values.syndeseis)
																	? [...values.syndeseis]
																	: (values.syndeseis || "")
																			.split(",")
																			.filter(Boolean);
																if (checked) {
																	newArr.push(option);
																} else {
																	newArr = newArr.filter((v) => v !== option);
																}
																setValues((v) => ({ ...v, syndeseis: newArr }));
															}}
															name={option}
														/>
													}
													label={option}
												/>
											))}
									</FormGroup>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
					<Accordion
						sx={{ width: "100%", my: 2, boxShadow: "none", border: "none" }}
						expanded={expandedAccordions.property}
						onChange={handleAccordionChange("property")}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse" }}>
							<Typography sx={{ fontWeight: "bold", color: "#003375", fontSize: "14px" }}>
								Στοιχεία Ακινήτου
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={2}>
								{propertyAccordionFields
									.filter((fieldName) => fieldName !== "paratiriseis")
									.map((fieldName) => {
										const f = fields.find((f) => f.name === fieldName);
										if (!f) return null;
										if (f.name === "emvado_ktimatos") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "number"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "pososto_kalypsis") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "number"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "syntelestis_domisis") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "number"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "foreas_idioktitis") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "foreas_diaxeirisis") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "xristis_akinitou") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "antikeimeniki_axia") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "number"}
														value={values[f.name] || ""}
														onChange={handleChange}
														slotProps={{
															input: {
																startAdornment: (
																	<InputAdornment position="start">€</InputAdornment>
																),
															},
														}}
													/>
												</Grid>
											);
										}
										if (f.name === "imnia_ypologismou_axias") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<DatePicker
														label={f.label}
														value={values[f.name]}
														onChange={handleDateChange(f.name)}
														format="DD/MM/YYYY"
														slotProps={{
															textField: {
																fullWidth: true,
															},
														}}
													/>
												</Grid>
											);
										}
										if (f.name === "misthoma") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "number"}
														value={values[f.name] || ""}
														onChange={handleChange}
														slotProps={{
															input: {
																startAdornment: (
																	<InputAdornment position="start">€</InputAdornment>
																),
															},
														}}
													/>
												</Grid>
											);
										}
										if (f.name === "imnia_enarxis_misthotiriou") {
											return (
												<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
													<DatePicker
														label={f.label}
														value={values[f.name]}
														onChange={handleDateChange(f.name)}
														format="DD/MM/YYYY"
														slotProps={{
															textField: {
																fullWidth: true,
															},
														}}
													/>
												</Grid>
											);
										}
										if (f.name === "empragmata_dikaiomata") {
											return (
												<Grid size={{ xs: 12 }} key={f.name}>
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										if (f.name === "paratiriseis") {
											return (
												<Grid size={{ xs: 12 }} key={f.name} sx={{ width: "100%" }}>
													<TextField
														fullWidth
														multiline
														minRows={4}
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
													/>
												</Grid>
											);
										}
										return (
											<Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
												{f.type === "date" ? (
													<DatePicker
														label={f.label}
														value={values[f.name]}
														onChange={handleDateChange(f.name)}
														format="DD/MM/YYYY"
														slotProps={{
															textField: {
																fullWidth: true,
															},
														}}
													/>
												) : f.type === "multiselect" ? (
													<Autocomplete
														multiple
														options={f.options}
														value={values[f.name] || []}
														onChange={(_, newValue) =>
															setValues((v) => ({ ...v, [f.name]: newValue }))
														}
														disableCloseOnSelect
														renderOption={(props, option, { selected }) => (
															<li {...props}>
																<input
																	type="checkbox"
																	checked={selected}
																	readOnly
																	style={{ marginRight: 8 }}
																/>
																{option}
															</li>
														)}
														renderInput={(params) => (
															<TextField {...params} label={f.label} fullWidth />
														)}
													/>
												) : (
													<TextField
														fullWidth
														label={f.label}
														name={f.name}
														type={f.type || "text"}
														value={values[f.name] || ""}
														onChange={handleChange}
														InputLabelProps={undefined}
														disabled={f.name === "aa"}
														InputProps={f.name === "aa" ? { readOnly: true } : undefined}
													/>
												)}
											</Grid>
										);
									})}
								{(() => {
									const f = fields.find((f) => f.name === "paratiriseis");
									if (!f) return null;
									return (
										<Grid size={{ xs: 12 }} key={f.name} sx={{ width: "100%" }}>
											<TextField
												fullWidth
												multiline
												minRows={4}
												label={f.label}
												name={f.name}
												type={f.type || "text"}
												value={values[f.name] || ""}
												onChange={handleChange}
											/>
										</Grid>
									);
								})()}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>
				<Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
					<Button onClick={onCancel} sx={{ textTransform: "none", fontWeight: 500 }}>
						Ακύρωση
					</Button>
					<Button
						type="submit"
						variant="contained"
						sx={{ textTransform: "none", fontWeight: 500 }}
						loadingPosition="start"
						loading={savingStatus}
					>
						Αποθήκευση
					</Button>
				</Stack>
			</form>
		</LocalizationProvider>
	);
}
